import { get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    TextAlignType,
    TextWritingType,
    type EditingRange,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';
import { pushHistory, RichTextProcessor, updateGroupLayer, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

export interface IFontSize {
    /**
     * 设置字体大小
     * @param layerID - 图层 ID
     * @param fontSize - 字体大小
     * @param options - 配置项
     * @returns
     */
    __setFontSize(
        layerID: string,
        fontSize: number,
        options?: { range?: EditingRange | null; writeHistory?: boolean; clearPrevLayer?: boolean },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null } | void>;
}

export class FontSize implements IFontSize {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private calcLayout(prevLayer: ILayerProps, nextLayer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = nextLayer;
        const layerData = nextLayer.data as ITextLayerProps;
        const { fontSize, writingMode, textAlign } = layerData;

        const measureOptions = {
            keepConstantWidth: false,
            keepConstantHeight: false,
        };

        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;
        const fixedDirectionSize = get(nextLayer, '__fixedDirectionSize', false);

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const isHorizontalMultiLine = checkIsHorizontalMultiLine(
            prevLayer.height,
            prevLayerData.fontSize,
            prevLayerData.lineSpacing,
        );

        if (layerWidth > fontSize && isHorizontal && (fixedDirectionSize || isHorizontalMultiLine)) {
            measureOptions.keepConstantWidth = true;
        }

        const textSize = TextMeasurer.measure(nextLayer, measureOptions);

        const nextLayerWidth = textSize.width;
        const nextLayerHeight = textSize.height;

        let nextLayerTop = nextLayer.top + (layerHeight - nextLayerHeight) / 2;
        if (isHorizontal && isHorizontalMultiLine) {
            nextLayerTop = nextLayer.top;
        }

        let nextLayerLeft = nextLayer.left;
        if (textAlign === TextAlignType.CENTER) {
            nextLayerLeft = nextLayerLeft + (layerWidth - nextLayerWidth) / 2;
        } else if (textAlign === TextAlignType.RIGHT) {
            nextLayerLeft = nextLayerLeft + (layerWidth - nextLayerWidth);
        }

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
            top: nextLayerTop,
            left: nextLayerLeft,
        };
    }

    private async set(
        prevLayer: ILayerProps,
        fontSize: number,
        options: { range?: EditingRange | null; update?: boolean; writeHistory?: boolean } = {},
    ) {
        const { update = false, writeHistory = false } = options;
        const range = options.range || null;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        let nextLayerData: ITextLayerProps = { ...prevLayerData };

        let nextRange: EditingRange | null = range;

        if (range && !range.isCollapsed) {
            const result = RichTextProcessor.process(nextLayerData, range, {
                key: 'fontSize',
                value: fontSize,
            });
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            const { fontSize: prevFontSize } = prevLayerData;
            if (prevFontSize === fontSize) return;

            nextLayerData = { ...nextLayerData, fontSize };

            const { textContents: prevTextContents = [] } = prevLayerData;
            if (prevTextContents.length > 0) {
                const nextTextContents = prevTextContents.map((item) => ({
                    ...item,
                    fontSize,
                }));
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }
        }

        // const ratio = fontSize / prevLayerData.fontSize;

        // // 若存在描边，需要对描边的宽度进行等比缩放
        // if (prevLayerData.textStroke && prevLayerData.textStroke.length > 0) {
        //     const nextTextStrokes = prevLayerData.textStroke.map((stroke) => ({
        //         ...stroke,
        //         width: Math.round(stroke.width * ratio),
        //     }));
        //     nextLayerData = { ...nextLayerData, textStroke: nextTextStrokes };
        // }

        // // 若存在投影，需要对描边的x、y进行等比缩放
        // if (prevLayerData.textShadow && prevLayerData.textShadow.length > 0) {
        //     const nextTextShadows = prevLayerData.textShadow.map((shadow) => ({
        //         ...shadow,
        //         x: Math.round(shadow.x * ratio),
        //         y: Math.round(shadow.y * ratio),
        //     }));
        //     nextLayerData = { ...nextLayerData, textShadow: nextTextShadows };
        // }

        // // 若存在外发光，需要对描边的宽度进行等比缩放
        // if (prevLayerData.textGlow && prevLayerData.textGlow.length > 0) {
        //     const nextTextGlows = prevLayerData.textGlow.map((glow) => ({
        //         ...glow,
        //         width: Math.round(glow.width * ratio),
        //     }));
        //     nextLayerData = { ...nextLayerData, textGlow: nextTextGlows };
        // }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            const { width, height, top, left } = this.calcLayout(prevLayer, nextLayer);
            nextLayer.width = width;
            nextLayer.height = height;
            nextLayer.top = top;
            nextLayer.left = left;
            nextLayer.__fixedDirectionSize = get(prevLayer, '__fixedDirectionSize', false);
        } else {
            if (writeHistory) {
                const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
                if (layer) nextLayer = layer;
                updateLayer(this.framework, nextLayer);
            }
        }

        updateLayer(this.framework, nextLayer);
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].size);

        return {
            layer: nextLayer,
            range: nextRange,
        };
    }

    async __setFontSize(
        layerID: string,
        fontSize: number,
        options = { range: null, writeHistory: false, clearPrevLayer: false },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        const { range, writeHistory, clearPrevLayer } = options;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(children.map((child) => this.set(child, fontSize)));
            const layers = results.map((result) => (result ? result.layer : null)).filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER].size,
                update: !writeHistory,
                writeHistory,
            });

            if (writeHistory || clearPrevLayer) this.prevLayer = null;

            return { layer: nextLayer, range: null };
        }

        const result = await this.set(prevLayer, fontSize, { range, update: !writeHistory, writeHistory });
        if (writeHistory || clearPrevLayer) this.prevLayer = null;

        return result;
    }
}
